import React, { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Button,
    Input,
    Label,
    useToast,
} from '@t-one-internal/design-system'
import { recognitionService } from '../../../services/recognitionService'
import type { CompanyValueOption } from '../../../api/recognitionApi'

interface AddCompanyValueModalProps {
    open: boolean
    onClose: () => void
    onCreated?: (value: CompanyValueOption) => void
}

const AddCompanyValueModal: React.FC<AddCompanyValueModalProps> = ({ open, onClose, onCreated }) => {
    const { toast } = useToast()
    const [name, setName] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const reset = () => {
        setName('')
        setError('')
        setIsSubmitting(false)
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim()) {
            setError('Name is required.')
            return
        }
        setError('')
        setIsSubmitting(true)
        try {
            const created = await recognitionService.createCompanyValue({
                name: name.trim(),
            })
            toast({ title: 'Company value created!', description: `"${created.name}" has been added.` })
            onCreated?.(created)
            handleClose()
        } catch {
            setError('Failed to create company value. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o: boolean) => !o && handleClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Add Company Value</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                    {/* Name */}
                    <div className="space-y-1.5">
                        <Label htmlFor="cv-name">Name <span className="text-destructive">*</span></Label>
                        <Input
                            id="cv-name"
                            placeholder="e.g. Integrity"
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setName(e.target.value); setError('') }}
                            autoFocus
                        />
                        {error && <p className="text-xs text-destructive">{error}</p>}
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Company values represent the core principles of your organization. They can be tagged to recognitions to highlight which value a colleague demonstrated.
                    </p>

                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting || !name.trim()}>
                            {isSubmitting ? 'Creating…' : 'Create Value'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default AddCompanyValueModal
